import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/polymer/lib/elements/dom-if.js';
import {timeOut} from '@polymer/polymer/lib/utils/async.js';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {ScopeBinding} from './behaviors/keli-binding-behavior.js';
import {HotkeyBehavior} from './behaviors/hotkey-behavior.js';
import {MarkdownSharedStylesHTML} from './shared-styles/markdown.js';
import {renderKeliMarkdown} from '../javascripts/modules/renderKeliMarkdown.js';
import {backgroundColorValue, desiredTextColor, foregroundColorValue} from '../javascripts/modules/colors.js';
import '@shoelace-style/shoelace/dist/components/skeleton/skeleton.js';

class KeliMarkdown extends ScopeBinding(HotkeyBehavior(PolymerElement)) {
  static get template() {
    return html`
      ${MarkdownSharedStylesHTML}
      <style>
        :host {
          position: relative;
          overflow: visible;
          display: block;
          color: var(--keli-markdown-text-color, var(--keli-text));
          background-color: var(--keli-markdown-background-color, transparent);
          font-size: var(--keli-markdown-font-size, medium);
          text-align: var(--keli-markdown-text-align, start);
        }

        :host(.editing) {
          display: flex;
          border: 2px dotted var(--keli-theme-accent-300);
        }

        :host(.editing) #textContainer {
          flex: 1;
          min-height: 10em;
          max-height: 30em;
          overflow-y: scroll;
          padding: .5em;
        }

        :host(.editing) #editContainer {
          flex: 1;
        }

        #editAction {
          display: none;
          cursor: pointer;
          position: absolute;
          top: 0;
          right: 0;
          padding: 5px;
          color: var(--keli-a-color);
          background-color: var(--keli-background-color);
          text-decoration: underline;
        }

        :host(:hover) #editAction {
          display: inline-block;
        }

        :host(.editing) #editAction {
          display: none;
        }

        #editContent {
          border: none;
          box-sizing: border-box;
          font-family: inherit;
          font-size: .9em;
          font-weight: 300;
          height: calc(100% - 50px);
          outline: none;
          padding: .5em;
          resize: none;
          width: 100%;
          background-color: var(--keli-theme-yellow-100);
          color: var(--keli-text-dark);
        }
      </style>

      <template is="dom-if" if="[[editable]]">
        <span id="editAction" on-click="edit">Edit</span>
      </template>
      <div id="textContainer">
        <slot></slot>
      </div>
      <template is="dom-if" if="[[editing]]">
        <div id="editContainer">
          <textarea id="editContent" on-input="handleEditInput" on-keydown="handleEditKeyDown" on-blur="save"
                    value="{{currentValue::input}}"></textarea>
          <keli-button on-activate="finish" caption="Save & Finish" design="flat" background="600"></keli-button>
        </div>
      </template>`;
  }

  static get is() {
    return 'keli-markdown';
  }

  static get properties() {
    return {
      align: {type: String, observer: 'alignChanged', scopevar: true},
      background: {type: String, scopevar: true, observer: 'backgroundChanged'},
      blankSlate: {type: String, observer: 'renderDisplayValue', scopevar: true},
      color: {type: String, observer: 'colorChanged', scopevar: true},
      db: {type: Boolean, scopevar: true},
      currentValue: {type: String, notify: true, observer: 'renderDisplayValue'},
      displayValue: {type: String, observer: 'displayValueChanged', scopevar: {options: {peek: false}}},
      editable: {type: Boolean, computed: 'computeEditable(isField, db, ro)'},
      editing: {type: Boolean, observer: 'editingChanged'},
      fireKeliAccepted: Boolean,
      fontScale: {type: String, observer: 'fontScaleChanged', scopevar: true},
      isBlockPanelElement: {type: Boolean, value: true},
      isField: {type: Boolean, scopevar: true},
      keliControlTypes: {type: Array, value: ['markdown']},
      needsDataLoaded: {type: Boolean, value: true},
      ro: {type: Boolean, scopevar: true}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli(['keliAccepted']);
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.textContent && this.textContent.trim()) {
      this.ro = true;
      this.displayValue = this.textContent;
    }

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.markdown.enter, {
      down: event => {
        event.stopImmediatePropagation();
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.markdown.finish, {
      down: event => {
        event.stopImmediatePropagation();
        this.editing = false;
      }
    }, 'raw-listener');
  }

  computeEditable(isField, db, ro) {
    return db !== false && ro !== true && isField !== false;
  }

  displayValueChanged(newValue) {
    this.currentValue = newValue;
    this.renderDisplayValue();
  }

  renderDisplayValue() {
    const value = this.currentValue?.trim() || this.displayValue?.trim() || this.blankSlate || '';
    if (value) {
      renderKeliMarkdown(value, this.$.textContainer);
    } else {
      this.$.textContainer.textContent = '';
    }
  }

  alignChanged(newValue) {
    this.updateStyles({'--keli-markdown-text-align': newValue || ''});
  }

  backgroundChanged(newValue) {
    const backgroundColor = backgroundColorValue(newValue);
    this.updateStyles({
      '--keli-markdown-background-color': backgroundColor,
      '--keli-markdown-text-color': foregroundColorValue(this.color, null, true, backgroundColor) || desiredTextColor(backgroundColor)
    });
  }

  colorChanged(newValue) {
    this.updateStyles({'--keli-markdown-text-color': foregroundColorValue(newValue, this)});
  }

  fontScaleChanged(newValue) {
    const size = parseFloat(newValue);
    this.updateStyles({'--keli-markdown-font-size': (!isNaN(size) ? `${size}em` : '')});
  }

  edit() {
    this.editing = true;
    this.currentValue = this.displayValue;
    afterNextRender(this, () => {
      this.shadowRoot.querySelector('#editContent').focus();
    });
  }

  finish() {
    this.editing = false;
    this.save();
  }

  save() {
    if (this.fireKeliAccepted) {
      this.keliAccept();
    }
  }

  editingChanged(newValue) {
    this.classList.toggle('editing', newValue);
  }

  handleEditKeyDown(event) {
    if ([KeliInterface.Hotkeys.arrows.up, KeliInterface.Hotkeys.arrows.down].includes(event.code)) {
      event.stopPropagation();
    }
    if ([KeliInterface.Hotkeys.arrows.up, KeliInterface.Hotkeys.arrows.down].includes(event.code)) {
      event.stopPropagation();
    }
  }

  handleEditInput() {
    this.fireKeliAccepted = true;
    this._handleEditInputJob = Debouncer.debounce(this._handleEditInputJob, timeOut.after(5000), () => this.save());
  }

  keliAccept(event) {
    this.displayValue = this.currentValue;

    if (this.fireKeliAccepted) {
      this.dispatchEvent(new CustomEvent('keliAccepted', {
        detail: {element: this, event, value: this.displayValue}
      }));
      this.fireKeliAccepted = false;
    }
  }
}

customElements.define(KeliMarkdown.is, KeliMarkdown);
export {KeliMarkdown};
